import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  shareReplay,
  type Observable,
} from 'rxjs';
import type { ViewIds } from '@ardoq/api-types';
import { shallowEqual } from 'utils/objectUtils';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import type { ViewpointModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { warmConditionalFormattingFilterCache } from 'tabview/bubbleChart/sourceData/buildViewpointModeViewModelSourceData';
import { buildPoints3DSourceData } from '../sourceData/buildPoints3DSourceData';
import type { Points3DViewProps } from '../Points3DView';
import {
  toPlottedViewEffects$,
  toStableSelection$,
} from 'tabview/chartShared/plottedViewEffects';
import { XYZ_DERIVATION_INPUTS, toXyzFieldSelection } from './xyzDerivation';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { toPoints3DLegendRows } from '../points3dLegend';

export type PointsViewId = ViewIds.SCATTER_3D | ViewIds.BUBBLE_3D;

/**
 * Everything one of the two point charts is rendered from.
 *
 * `viewId` and `showSizeVariation` are the whole difference between scatter and bubble, and
 * neither is a setting: each is registered behind its own flag, and a dropdown that turned one
 * into the other would make two registrations pointless. They are constants of the view model
 * rather than stream values.
 */
export const points3dViewModel$ = (
  viewInstanceId: string,
  viewId: PointsViewId,
  showSizeVariation: boolean,
  viewSettingsWithChanges$: Observable<
    ViewpointModeInstanceViewSettings<PointsViewId>
  >
): Observable<Points3DViewProps> => {
  const viewSettings$ = viewSettingsWithChanges$.pipe(
    map(({ currentSettings }) => currentSettings)
  );

  const sourceData$ = combineLatest({
    ...XYZ_DERIVATION_INPUTS,
    /* Narrowed, so a ramp, a band count or a mark-size toggle cannot re-derive every point - and
       so the hover and the tooltip survive a settings write under a cursor that never moved. */
    viewSettings: toStableSelection$(viewSettings$, toXyzFieldSelection),
  }).pipe(
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    distinctUntilChanged(shallowEqual),
    map(args =>
      buildPoints3DSourceData(viewInstanceId, {
        ...args,
        /* Warmed in the same step that reads it; see `contourViewModel$`. */
        filterColorData: warmConditionalFormattingFilterCache(args.loadedGraph),
      })
    ),
    map(sourceData => ({
      ...sourceData,
      legendRows: toPoints3DLegendRows(
        sourceData.legend,
        sourceData.i18nLocale
      ),
      viewLegendLabels: getViewLegendLabels(sourceData.i18nLocale),
    })),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  return merge(
    combineLatest([sourceData$, viewSettings$]).pipe(
      map(([sourceData, viewSettings]) => ({
        ...sourceData,
        viewSettings,
        viewId,
        showSizeVariation,
      }))
    ),
    toPlottedViewEffects$(sourceData$, viewId)
  );
};
